import {
  BookOpenIcon,
  ChevronRightIcon,
  CircleStackIcon as DatasetIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon as NotebookIcon,
  InformationCircleIcon,
  KeyIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserCircleIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { ChevronUpIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import {
  Form,
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useRouteLoaderData,
  useSubmit,
} from "react-router";
import { $path, $routeId } from "safe-routes";
import {
  GaugeIcon,
  DollarSignIcon,
  FlaskConicalIcon,
  PackageIcon,
  PlugIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "~/components/ui/sidebar";
import { PythonLogo } from "~/components/icons/PythonLogo";
import { RLogo } from "~/components/icons/RLogo";

import AppLink from "./components/appLink";
import { Button } from "./components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Tooltip } from "./components/ui/tooltip";
import type { SessionUser } from "./lib/authentication.server";
import { cn } from "./lib/utils";
import { NewNotebookModal } from "./sidebar/NewNotebookModal";
import { useIsMobile } from "./hooks/useMobile";
import Logo from "./components/icons/Logo";
import { match } from "ts-pattern";
import { WorkspaceSwitcher } from "./components/WorkspaceSwitcher";

export function AppSidebar({ user }: { user: SessionUser | undefined }) {
  const { state } = useSidebar();

  const location = useLocation();
  const isMobile = useIsMobile();

  const { isSynthesizeEmployee } =
    useRouteLoaderData<{ isSynthesizeEmployee: boolean }>($routeId("root")) ?? {};
  const isStaff = !!isSynthesizeEmployee;

  enum tab {
    notebooks = "/notebooks",
    datasets = "/datasets",
    staff = "/staff",
  }

  const currentTab = match(location.pathname)
    .when(
      (path) => path.startsWith($path("/notebooks")),
      () => tab.notebooks,
    )
    .when(
      (path) => path.startsWith("/staff"),
      () => tab.staff,
    )
    .when(
      (path) => path.startsWith($path("/account")),
      () => "",
    )
    .when(
      (path) => path.startsWith("/sign-in"),
      () => "",
    )
    .otherwise(() => tab.datasets);

  type MenuItem = {
    title: string;
    icon: any;
    url: string;
    className?: string;
    matchSearchParams?: string[];
    requiresAuth?: boolean;
  };

  const notebookMenuItems: MenuItem[] = [
    {
      title: "All Notebooks",
      icon:
        state === "expanded" ? (
          <Squares2X2Icon />
        ) : (
          <Tooltip title="All Notebooks" side="right">
            <Squares2X2Icon />
          </Tooltip>
        ),
      url: "/notebooks",
    },
    {
      title: "My Notebooks",
      icon:
        state === "expanded" ? (
          <UserIcon />
        ) : (
          <Tooltip title="My Notebooks" side="right">
            <UserIcon />
          </Tooltip>
        ),
      url: "/notebooks/created-by-me",
      requiresAuth: true,
    },
    {
      title: "Shared with me",
      icon:
        state === "expanded" ? (
          <UsersIcon />
        ) : (
          <Tooltip title="Shared with me" side="right">
            <UsersIcon />
          </Tooltip>
        ),
      url: "/notebooks/shared-with-me",
      requiresAuth: true,
    },
  ];

  const datasetMenuItems: MenuItem[] = [
    {
      title: "New AI Dataset",
      icon:
        state === "expanded" ? (
          <SparklesIcon />
        ) : (
          <Tooltip title="New AI Dataset" side="right">
            <SparklesIcon />
          </Tooltip>
        ),
      url: "/datasets/generate",
    },
    {
      title: "All Datasets",
      icon:
        state === "expanded" ? (
          <Squares2X2Icon />
        ) : (
          <Tooltip title="All Datasets" side="right">
            <Squares2X2Icon />
          </Tooltip>
        ),
      url: "/datasets",
      matchSearchParams: ["show"],
    },
    {
      title: "My Datasets",
      icon:
        state === "expanded" ? (
          <UserIcon />
        ) : (
          <Tooltip title="My Datasets" side="right">
            <UserIcon />
          </Tooltip>
        ),
      url: "/datasets?show=owned",
      matchSearchParams: ["show"],
      requiresAuth: true,
    },
    {
      title: "Shared with me",
      icon:
        state === "expanded" ? (
          <UsersIcon />
        ) : (
          <Tooltip title="Shared with me" side="right">
            <UsersIcon />
          </Tooltip>
        ),
      url: "/datasets?show=shared",
      matchSearchParams: ["show"],
      requiresAuth: true,
    },
  ];

  const staffMenuItems: MenuItem[] = [
    {
      title: "Model rate limits",
      icon:
        state === "expanded" ? (
          <GaugeIcon />
        ) : (
          <Tooltip title="Model rate limits" side="right">
            <GaugeIcon />
          </Tooltip>
        ),
      url: "/staff/model-rate-limits",
    },
    {
      title: "Model releases",
      icon:
        state === "expanded" ? (
          <PackageIcon />
        ) : (
          <Tooltip title="Model releases" side="right">
            <PackageIcon />
          </Tooltip>
        ),
      url: "/staff/model-releases",
    },
    {
      title: "Model endpoints",
      icon:
        state === "expanded" ? (
          <PlugIcon />
        ) : (
          <Tooltip title="Model endpoints" side="right">
            <PlugIcon />
          </Tooltip>
        ),
      url: "/staff/model-endpoints",
    },
    {
      title: "Dataset users",
      icon:
        state === "expanded" ? (
          <UsersIcon />
        ) : (
          <Tooltip title="Dataset users" side="right">
            <UsersIcon />
          </Tooltip>
        ),
      url: "/staff/dataset-users",
    },
    {
      title: "Clinical trial predictions",
      icon:
        state === "expanded" ? (
          <FlaskConicalIcon />
        ) : (
          <Tooltip title="Clinical trial predictions" side="right">
            <FlaskConicalIcon />
          </Tooltip>
        ),
      url: "/staff/clinical-trial-predictions",
    },
    {
      title: "AI spend",
      icon:
        state === "expanded" ? (
          <DollarSignIcon />
        ) : (
          <Tooltip title="AI spend" side="right">
            <DollarSignIcon />
          </Tooltip>
        ),
      url: "/staff/ai-spend",
    },
  ];

  const filterMenuItems = (items: MenuItem[]) => {
    if (user) return items;
    return items.filter((item) => !item.requiresAuth);
  };

  const menuItems = match(currentTab)
    .with(tab.datasets, () => filterMenuItems(datasetMenuItems))
    .with(tab.notebooks, () => filterMenuItems(notebookMenuItems))
    .with(tab.staff, () => staffMenuItems)
    .otherwise(() => []);

  return (
    <Sidebar
      collapsible={isMobile ? "offcanvas" : "icon"}
      className={cn("bg-muted transition-all duration-300 ease-in-out pt-4 pb-2", {
        "px-2": state === "expanded",
      })}
    >
      <AppSidebarHeader
        user={user}
        isStaff={isStaff}
        isStaffActive={currentTab === tab.staff}
      />
      <SidebarContent
        className={cn("p-2", {
          "pt-6": state === "expanded",
        })}
      >
        <Tabs defaultValue={currentTab} value={currentTab}>
          <TabsList
            className={cn("w-full h-auto mb-4", {
              "grid grid-cols-2": state === "expanded",
              "flex flex-col": state === "collapsed" && !isMobile,
              border: state === "expanded",
            })}
          >
            <TabsTrigger
              className="flex-1 data-[state=active]:shadow-none"
              value={tab.datasets}
              asChild
            >
              <NavLink to={$path("/datasets/generate")} className="text-muted-foreground">
                {state === "collapsed" ? (
                  <Tooltip title="Datasets" side="right">
                    <DatasetIcon className="w-5 h-5" />
                  </Tooltip>
                ) : (
                  "Datasets"
                )}
              </NavLink>
            </TabsTrigger>
            <TabsTrigger
              className="flex-1 data-[state=active]:shadow-none"
              value={tab.notebooks}
              asChild
            >
              <NavLink to={$path("/notebooks")} className="text-muted-foreground">
                {state === "collapsed" ? (
                  <Tooltip title="Notebooks" side="right">
                    <NotebookIcon className="w-5 h-5" />
                  </Tooltip>
                ) : (
                  "Notebooks"
                )}
              </NavLink>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <SidebarMenu>
          {menuItems.map((item) => (
            <SidebarMenuItem key={item.title} className={item.className || ""}>
              <SidebarMenuButton asChild>
                <AppLink
                  to={item.url}
                  matchHash={true}
                  matchSearchParams={item.matchSearchParams}
                  className=""
                  activeClassName="bg-white hover:bg-white"
                >
                  {item.icon}
                  <span>{item.title}</span>
                </AppLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
          {currentTab === tab.datasets && <ApiAccessMenuItem />}
        </SidebarMenu>
      </SidebarContent>
      <AppSidebarFooter user={user} />
    </Sidebar>
  );
}

function ApiAccessMenuItem() {
  const { state } = useSidebar();
  const location = useLocation();
  const isGettingStartedActive = location.pathname === "/datasets/api-getting-started";
  const isApiKeysActive = location.pathname === "/account/api-keys";
  const hasActiveChild = isGettingStartedActive || isApiKeysActive;
  const [isOpen, setIsOpen] = useState(hasActiveChild);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton onClick={() => setIsOpen((prev) => !prev)} className="cursor-pointer">
        {state === "expanded" ? (
          <CodeBracketIcon />
        ) : (
          <Tooltip title="API access" side="right">
            <CodeBracketIcon />
          </Tooltip>
        )}
        <span>API access</span>
        <ChevronRightIcon
          className={cn(
            "ml-auto h-4 w-4 transition-transform duration-200",
            isOpen && "rotate-90",
          )}
        />
      </SidebarMenuButton>
      {isOpen && (
        <SidebarMenuSub className="mr-0 pr-0">
          <SidebarMenuSubItem>
            <SidebarMenuSubButton asChild isActive={isGettingStartedActive}>
              <AppLink
                to="/datasets/api-getting-started"
                activeClassName={isGettingStartedActive ? "bg-white hover:bg-white" : ""}
              >
                <InformationCircleIcon className="h-4 w-4" />
                <span>Getting started</span>
              </AppLink>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
          <SidebarMenuSubItem>
            <SidebarMenuSubButton asChild>
              <a
                href="https://github.com/synthesizebio/pysynthbio"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PythonLogo width={16} height={16} />
                <span>Python package</span>
              </a>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
          <SidebarMenuSubItem>
            <SidebarMenuSubButton asChild>
              <a
                href="https://github.com/synthesizebio/rsynthbio/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <RLogo width={16} height={12} />
                <span>R library</span>
              </a>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
          <SidebarMenuSubItem>
            <SidebarMenuSubButton asChild isActive={isApiKeysActive}>
              <Link to="/account/api-keys" target="_blank" rel="noopener noreferrer">
                <KeyIcon className="h-4 w-4" />
                <span>API keys</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

export function AppSidebarMobileHeader() {
  return (
    <div className="flex justify-between items-center p-4 md:hidden w-full">
      <a href="/" className="flex items-center gap-2 text-sbBlack">
        <Logo title="Synthesize.bio logo" showTitleText={false} width={40} />
      </a>
      <SidebarTrigger variant="ghost" />
    </div>
  );
}

export function AppSidebarHeader({
  user,
  isStaff,
  isStaffActive,
}: {
  user?: SessionUser;
  isStaff?: boolean;
  isStaffActive?: boolean;
}) {
  const { state } = useSidebar();

  const isMobile = useIsMobile();
  const isCollapsed = state === "collapsed" && !isMobile;

  return (
    <SidebarHeader>
      <div
        className={cn(
          "flex justify-between items-center gap-2 [transition:margin_25ms_ease] pt-2 md:pt-0",
          { "justify-center -mr-2": isCollapsed },
        )}
      >
        <Link
          to="/"
          className={cn("text-sbBlack flex items-center gap-2", {
            hidden: isCollapsed,
          })}
        >
          <Logo width={160} title="Synthesize.bio logo" />
        </Link>
        <div
          className={cn("flex items-center", {
            "-ml-[10px]": isCollapsed,
            "flex-col": isCollapsed,
            "flex-row-reverse": !isCollapsed,
          })}
        >
          <Tooltip
            title={state === "collapsed" ? "Expand sidebar" : "Collapse sidebar"}
            side="right"
          >
            <SidebarTrigger variant="ghost" />
          </Tooltip>
          {isStaff && (
            <Tooltip title="Staff" side="right">
              <Link
                to="/staff"
                className={cn(
                  "inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors",
                  { "text-foreground bg-white": isStaffActive },
                )}
              >
                <Cog6ToothIcon className="w-5 h-5" />
              </Link>
            </Tooltip>
          )}
        </div>
      </div>
      {!isCollapsed && user && (
        <div className="mt-2">
          <WorkspaceSwitcher />
        </div>
      )}
    </SidebarHeader>
  );
}

export function AppSidebarFooter({ user }: { user: SessionUser | undefined }) {
  return (
    <SidebarFooter className="p-4 md:p-2">
      <SidebarMenu>
        <BackToNotebooksButton user={user} />
        <NewNotebookButton user={user} />
        <DocumentationLink />
        {user && <UserSidebarMenu user={user} />}
        {!user && <SignInLink />}
      </SidebarMenu>
    </SidebarFooter>
  );
}

function DocumentationLink() {
  const { state } = useSidebar();

  const link = (
    <a
      href="https://docs.synthesize.bio"
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center overflow-hidden p-1 rounded-lg w-full text-sbBlack text-sm hover:bg-white/60 transition-colors",
        { "px-0 justify-center": state === "collapsed" },
      )}
    >
      <BookOpenIcon
        className={cn("w-5 h-5 shrink-0", {
          "ml-1.5 mr-2": state === "expanded",
        })}
      />
      <span
        className={cn("whitespace-nowrap overflow-hidden transition-width ease-in-out", {
          "w-0": state === "collapsed",
          "w-full": state === "expanded",
        })}
      >
        Documentation
      </span>
    </a>
  );

  if (state === "collapsed") {
    return (
      <Tooltip title="Documentation" side="right">
        {link}
      </Tooltip>
    );
  }
  return link;
}

function SignInLink() {
  const location = useLocation();
  const { state } = useSidebar();
  const isActive = location.pathname.startsWith("/sign-in");

  return (
    <div
      className={cn("flex items-center mt-2 overflow-hidden p-1 rounded-lg", {
        "bg-white": isActive,
        "px-0": state === "collapsed",
      })}
    >
      <Link to={$path("/sign-in")} className="flex flex-1 items-center text-sbBlack text-sm">
        <div
          className={cn("mr-1 [transition:margin_300ms_ease]", {
            "-ml-0.5": state === "collapsed",
          })}
        >
          {state === "collapsed" ? (
            <Tooltip title="Sign in or Sign up" side="right">
              <UserCircleIcon className="w-9 h-9" strokeWidth={1} />
            </Tooltip>
          ) : (
            <UserCircleIcon className="w-9 h-9" strokeWidth={1} />
          )}
        </div>
        <div
          className={cn("overflow-hidden transition-width ease-in-out", {
            "w-0": state === "collapsed",
            "w-full": state === "expanded",
          })}
        >
          <div className="whitespace-nowrap">Sign in or Sign up</div>
        </div>
      </Link>
    </div>
  );
}

function BackToNotebooksButton(_props: { user: SessionUser | undefined }) {
  // Navigation is now always available in the sidebar, so this button is no longer needed
  return null;
}

function NewNotebookButton({ user }: { user: SessionUser | undefined }) {
  const params = useParams();
  const location = useLocation();

  // Only show for logged in users on notebook pages and listing pages
  if (!user) {
    return null;
  }

  const showButton =
    params.notebookId ||
    location.pathname === "/notebooks" ||
    location.pathname === "/notebooks/created-by-me" ||
    location.pathname === "/notebooks/shared-with-me";

  if (!showButton) {
    return null;
  }

  return (
    <SidebarMenuItem>
      <NewNotebookModal />
    </SidebarMenuItem>
  );
}

function UserSidebarMenu({ user }: { user: SessionUser | undefined }) {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const isAccountPage = location.pathname.startsWith($path("/account"));

  return (
    <>
      {user?.masqueradingUser && (
        <div>
          <p className="text-xs break-all">Masquerading as {user.email}</p>
          <Form method="post" action={$path("/account/masquerade/exit")}>
            <Button variant="destructive" className="h-auto">
              Stop masquerading
            </Button>
          </Form>
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center mt-2 overflow-hidden p-1 rounded-lg w-full text-left cursor-pointer hover:bg-white/60 transition-colors",
              {
                "bg-white": isAccountPage,
                "px-0": state === "collapsed",
              },
            )}
          >
            <div
              className={cn("mr-1 [transition:margin_300ms_ease]", {
                "-ml-0.5": state === "collapsed",
              })}
            >
              <UserCircleIcon className="w-9 h-9 text-sbBlack" strokeWidth={1} />
            </div>
            <div
              className={cn("overflow-hidden transition-width ease-in-out flex-col flex-1", {
                "w-0": state === "collapsed",
                "w-full": state === "expanded",
              })}
            >
              <div className="whitespace-nowrap text-sbBlack text-sm">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="opacity-50 text-sm">{user?.email}</div>
            </div>
            {state === "expanded" && (
              <ChevronUpIcon className="w-4 h-4 text-muted-foreground mr-1 shrink-0" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-56">
          <DropdownMenuItem onClick={() => navigate($path("/account"))}>
            <UserCircleIcon className="mr-2 h-4 w-4" />
            Account
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate($path("/account/usage"))}>
            <GaugeIcon className="mr-2 h-4 w-4" />
            Usage
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() =>
              submit(null, { method: "post", action: $path("/api/auth/signout") })
            }
          >
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
